import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import ConfirmModal from '../components/modal/ConfirmModal.tsx';
import './discount.scss';
import DiscountForm from './DiscountForm.tsx';
import { deleteDiscount, fetchDiscounts } from './discountService.ts';
import { Discount, PAYMENT_METHOD_LABELS } from './discountTypes.ts';

type DiscountStatus = 'active' | 'future' | 'expired';

const getDiscountStatus = (d: Discount): DiscountStatus => {
  const today = new Date().toISOString().slice(0, 10);
  if (!d.isActive || d.endDate < today) return 'expired';
  if (d.startDate > today) return 'future';
  return 'active';
};

const STATUS_LABELS: Record<DiscountStatus, string> = {
  active: 'Vigente',
  future: 'Próximo',
  expired: 'Vencido',
};

const DiscountList: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Discount | null>(null);

  const loadDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchDiscounts();
      setDiscounts(data);
    } catch {
      toast.error('Error al cargar descuentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDiscount(deleteTarget.id);
      toast.success(`Descuento "${deleteTarget.name}" eliminado`);
      setDeleteTarget(null);
      loadDiscounts();
    } catch {
      toast.error('Error al eliminar descuento');
    }
  };

  const handleFormClose = (refreshList?: boolean) => {
    setShowForm(false);
    setEditingDiscount(null);
    if (refreshList) loadDiscounts();
  };

  const handleEdit = (d: Discount) => {
    setEditingDiscount(d);
    setShowForm(true);
  };

  const formatDate = (date: string) => {
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
  };

  if (showForm) {
    return (
      <DiscountForm discount={editingDiscount} onClose={handleFormClose} />
    );
  }

  return (
    <div className="discount-section">
      {deleteTarget && (
        <ConfirmModal
          confirmText="Eliminar"
          bodyText={`¿Seguro que querés eliminar el descuento "${deleteTarget.name}"?`}
          action={handleDelete}
          cancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="discount-header">
        <h4 className="form-section-title" style={{ margin: 0 }}>
          Descuentos
        </h4>
        <button
          type="button"
          className="primary-button discount-add-btn"
          onClick={() => setShowForm(true)}
        >
          <FiPlus size={16} /> Nuevo descuento
        </button>
      </div>

      {loading ? (
        <p className="discount-empty">Cargando descuentos...</p>
      ) : discounts.length === 0 ? (
        <p className="discount-empty">No hay descuentos creados aún.</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="discount-table-wrapper">
            <table className="discount-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>%</th>
                  <th>Medios de pago</th>
                  <th>Desde</th>
                  <th>Hasta</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((d) => {
                  const status = getDiscountStatus(d);
                  return (
                    <tr key={d.id}>
                      <td>{d.name}</td>
                      <td className="discount-pct">{d.discountPercentage}%</td>
                      <td>
                        {d.paymentMethods
                          .map((m) => PAYMENT_METHOD_LABELS[m] || m)
                          .join(', ')}
                      </td>
                      <td>{formatDate(d.startDate)}</td>
                      <td>{formatDate(d.endDate)}</td>
                      <td>
                        <span
                          className={`discount-badge discount-badge--${status}`}
                        >
                          {STATUS_LABELS[status]}
                        </span>
                      </td>
                      <td>
                        <div className="discount-actions">
                          <button
                            type="button"
                            className="discount-action-btn discount-action-btn--edit"
                            onClick={() => handleEdit(d)}
                          >
                            <FiEdit2 size={15} />
                          </button>
                          <button
                            type="button"
                            className="discount-action-btn discount-action-btn--delete"
                            onClick={() => setDeleteTarget(d)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="discount-cards-mobile">
            {discounts.map((d) => {
              const status = getDiscountStatus(d);
              return (
                <div key={d.id} className="discount-card">
                  <div className="discount-card__top">
                    <div>
                      <span className="discount-card__name">{d.name}</span>
                      <span
                        className={`discount-badge discount-badge--${status}`}
                      >
                        {STATUS_LABELS[status]}
                      </span>
                    </div>
                    <span className="discount-card__pct">
                      {d.discountPercentage}%
                    </span>
                  </div>
                  <div className="discount-card__detail">
                    <span>
                      {d.paymentMethods
                        .map((m) => PAYMENT_METHOD_LABELS[m] || m)
                        .join(', ')}
                    </span>
                    <span>
                      {formatDate(d.startDate)} — {formatDate(d.endDate)}
                    </span>
                  </div>
                  <div className="discount-card__actions">
                    <button
                      type="button"
                      className="discount-action-btn discount-action-btn--edit"
                      onClick={() => handleEdit(d)}
                    >
                      <FiEdit2 size={14} /> Editar
                    </button>
                    <button
                      type="button"
                      className="discount-action-btn discount-action-btn--delete"
                      onClick={() => setDeleteTarget(d)}
                    >
                      <FiTrash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DiscountList;
